import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import SEO from '../../components/SEO';
import { parseSlokaRef } from '../../data/gita';
import { getSlokaAudioUrl } from '../../config/gitaAudio';
import './SlokaPage.css';

function SlokaPage() {
  const { slokaRef } = useParams();
  const sloka = parseSlokaRef(slokaRef);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [needsTap, setNeedsTap] = useState(false);

  const audioUrl = sloka ? getSlokaAudioUrl(sloka.ref) : null;

  // Best-effort autoplay. Most mobile browsers block unmuted sound until the
  // user taps something on this page — a QR open alone usually does not count.
  useEffect(() => {
    setPlaying(false);
    setError(null);
    setNeedsTap(false);

    const audio = audioRef.current;
    if (!audio || !audioUrl) return undefined;

    audio.pause();
    audio.currentTime = 0;

    let cancelled = false;
    const tryAutoplay = async () => {
      try {
        await audio.play();
        if (!cancelled) {
          setPlaying(true);
          setNeedsTap(false);
        }
      } catch {
        if (!cancelled) {
          setPlaying(false);
          setNeedsTap(true);
        }
      }
    };

    tryAutoplay();
    return () => {
      cancelled = true;
    };
  }, [slokaRef, audioUrl]);

  if (!sloka) {
    return <Navigate to="/" replace />;
  }

  const title = `Bhagavad Gita ${sloka.chapter}.${sloka.verse} | NAB India`;
  const description = `Listen to Bhagavad Gita Chapter ${sloka.chapter}, Sloka ${sloka.verse}.`;

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    try {
      if (audio.paused) {
        await audio.play();
        setPlaying(true);
        setNeedsTap(false);
        setError(null);
      } else {
        audio.pause();
        setPlaying(false);
      }
    } catch (err) {
      setPlaying(false);
      setNeedsTap(true);
      setError('Unable to play audio. Check the connection and try again.');
    }
  };

  return (
    <div className="sloka-page">
      <SEO
        title={title}
        description={description}
        keywords={`Bhagavad Gita, Gita, Chapter ${sloka.chapter}, Sloka ${sloka.verse}, NAB India`}
      />

      <p className="sloka-label">
        Chapter {sloka.chapter} · Sloka {sloka.verse}
      </p>

      {audioUrl ? (
        <>
          <audio
            ref={audioRef}
            src={audioUrl}
            preload="metadata"
            onEnded={() => setPlaying(false)}
            onPause={() => setPlaying(false)}
            onPlay={() => setPlaying(true)}
            onError={() => {
              setPlaying(false);
              setError('Audio not found. It may still be uploading.');
            }}
          />
          <button
            type="button"
            className="sloka-play-btn"
            onClick={togglePlayback}
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? (
              <span className="sloka-icon sloka-icon-pause" aria-hidden="true" />
            ) : (
              <span className="sloka-icon sloka-icon-play" aria-hidden="true" />
            )}
          </button>
          {needsTap && !error && (
            <p className="sloka-hint" aria-live="polite">
              Tap play to start listening
            </p>
          )}
        </>
      ) : (
        <p className="sloka-status">
          Audio for this sloka is not online yet.
        </p>
      )}

      {error && <p className="sloka-error" role="alert">{error}</p>}
    </div>
  );
}

export default SlokaPage;
